/**
 * Agent tab of a pe3d variant: describe a change in words, the agent makes it
 * (agentType "pe3d", src/server/controller/agent/domains/pe3d) — config values,
 * playable settings, slot files — through the same checks the editor's saves
 * go through. Same building blocks and credits as the other agent tabs
 * (modules/agentTabShared): prompt, result with undo, run history, the admin
 * console, and the continue dialog for long tasks.
 *
 * Every run, undo and restore changes the stored variant, so the editor reloads
 * it afterwards; the preview follows.
 */

import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@material-ui/core";
import {
  acknowledgeRun,
  fetchRunStatus,
  listAgentCheckpoints,
  pollRunUntilSettled,
  restoreAgentCheckpoint,
  runAgentOverSocket,
} from "../../modules/agentSocket";
import {
  AgentAdminConsole,
  AgentContinueDialog,
  AgentPromptCard,
  AgentResponseCard,
  AgentRestoreConfirmDialog,
  AgentRunningBanner,
  AgentVersionHistory,
} from "../../modules/agentTabShared";
import { handleAgentCreditsExhaustedError, hasAgentCreditsRemaining, notifyAgentCreditsExhausted } from "../../modules/agentPlanReset";

const AGENT_TYPE = "pe3d";

const responseOf = (result) => ({
  summary: result.summary,
  changes: result.changes || [],
  creditCost: result.creditCost != null ? result.creditCost : null,
  balanceAfter: result.balanceAfter != null ? result.balanceAfter : null,
  debugLog: result.debugLog || [],
  tokenUsage: result.tokenUsage || null,
  providerName: result.providerName || null,
  model: result.model || null,
  usdCost: result.usdCost != null ? result.usdCost : null,
  pricingExplanation: result.pricingExplanation || null,
});

const failureOf = (status, restored) => ({
  error: status.error || status.message || "Agent run failed",
  restored: Boolean(restored),
  creditCost: status.creditCost != null ? status.creditCost : null,
  balanceAfter: status.balanceAfter != null ? status.balanceAfter : null,
  pricingExplanation: status.pricingExplanation || null,
});

export default function ContainerPe3dAgent({ editor, userCompany, isAdmin, createSnackbar }) {
  const [prompt, setPrompt] = useState("");
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [remoteRunning, setRemoteRunning] = useState(false);
  const [liveLog, setLiveLog] = useState([]);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [checkpoints, setCheckpoints] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [confirmCheckpoint, setConfirmCheckpoint] = useState(null);
  const [continueInfo, setContinueInfo] = useState(null);
  const viewingRef = useRef(null);

  const variantId = editor.doc ? String(editor.doc._id) : null;
  viewingRef.current = variantId;
  const isViewing = (id) => viewingRef.current === id;
  const notify = (message, variant) => createSnackbar && createSnackbar(message, variant, false);

  async function fetchCheckpoints(id = variantId) {
    if (!id) return;
    try {
      const data = await listAgentCheckpoints(id, { agentType: AGENT_TYPE });
      if (data.ok && isViewing(id)) setCheckpoints(data.checkpoints || []);
    } catch (_err) {
      // The history drawer just stays empty.
    }
  }

  function applySuccess(result, id) {
    void acknowledgeRun(id);
    if (!isViewing(id)) {
      notify("Agent run finished on another variant", "success");
      return;
    }
    setResponse(responseOf(result));
    setCanUndo(Boolean(result.agentRunId));
    void editor.reload();
    void fetchCheckpoints(id);
    if (result.paused) setContinueInfo({ summary: result.summary, cumulativeTurns: result.cumulativeTurns, canContinue: result.canContinue !== false });
  }

  function applyFailure(status, id, restored) {
    void acknowledgeRun(id);
    if (!isViewing(id)) {
      notify("Agent run failed on another variant", "warning");
      return;
    }
    setResponse(failureOf(status, restored));
    void editor.reload();
  }

  // A run started earlier (another tab, a reload) is picked up where it is.
  useEffect(() => {
    setResponse(null);
    setCanUndo(false);
    setContinueInfo(null);
    setCheckpoints([]);
    if (!variantId) return undefined;
    let cancelled = false;
    const id = variantId;
    void fetchCheckpoints(id);
    fetchRunStatus(id)
      .then(async (status) => {
        if (cancelled || !status) return;
        if (status.status === "running") {
          setRemoteRunning(true);
          const settled = await pollRunUntilSettled(id);
          if (cancelled) return;
          setRemoteRunning(false);
          if (settled.status === "completed" && settled.result) applySuccess(settled.result, id);
          else if (settled.status === "failed") applyFailure(settled, id, settled.restored);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantId]);

  async function handleRun(opts = {}) {
    const isContinue = opts && opts.continueRun === true;
    if (!variantId || remoteRunning || (!isContinue && !prompt.trim())) return;
    if (!hasAgentCreditsRemaining(userCompany)) {
      notifyAgentCreditsExhausted(createSnackbar);
      return;
    }
    // The agent reads the stored variant: pending edits go first.
    if (editor.saveState !== "saved" && !(await editor.save())) {
      notify("Save or fix the variant's problems before running the agent.", "warning");
      return;
    }
    const id = variantId;
    setLoading(true);
    setResponse(null);
    setLiveLog([]);
    setCanUndo(false);
    setContinueInfo(null);
    try {
      const result = await runAgentOverSocket({
        agentType: AGENT_TYPE,
        versionId: id,
        prompt: isContinue ? "" : prompt.trim(),
        continueRun: isContinue,
        onEvent: isAdmin ? (event) => isViewing(id) && setLiveLog((previous) => [...previous, event]) : undefined,
      });
      applySuccess(result, id);
      if (!isContinue) setPrompt("");
    } catch (err) {
      if (handleAgentCreditsExhaustedError(err, createSnackbar)) return;
      if (err.code === "ALREADY_RUNNING" || err.isDisconnect) {
        setRemoteRunning(true);
        try {
          const settled = await pollRunUntilSettled(id);
          if (settled.status === "completed" && settled.result) applySuccess(settled.result, id);
          else if (settled.status === "failed") applyFailure(settled, id, settled.restored);
        } catch (pollErr) {
          if (isViewing(id)) setResponse({ error: pollErr.message || err.message });
        }
        return;
      }
      applyFailure(err, id, err.restored);
    } finally {
      setLoading(false);
      setRemoteRunning(false);
    }
  }

  async function restore(checkpointId) {
    const data = await restoreAgentCheckpoint(variantId, { agentType: AGENT_TYPE, checkpointId });
    if (!data.ok) throw new Error(data.error || "Nothing to restore");
    await editor.reload();
    await fetchCheckpoints(variantId);
  }

  async function handleUndo() {
    if (!variantId || undoing) return;
    setUndoing(true);
    try {
      await restore(null);
      setCanUndo(false);
      notify("Reverted to the variant before the agent run", "success");
    } catch (err) {
      notify(err.message || "Undo failed", "warning");
    } finally {
      setUndoing(false);
    }
  }

  async function confirmRestore() {
    const target = confirmCheckpoint;
    setConfirmCheckpoint(null);
    if (!target || !target.checkpointId) return;
    setRestoringId(target.checkpointId);
    try {
      await restore(target.checkpointId);
      setCanUndo(false);
      notify("Restored checkpoint", "success");
    } catch (err) {
      notify(err.message || "Restore failed", "error");
    } finally {
      setRestoringId(null);
    }
  }

  const debugLog = liveLog.length ? liveLog : response && response.debugLog;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%", boxSizing: "border-box" }}>
      {!variantId && <Typography variant="body2" style={{ fontSize: 12 }}>Select a variant.</Typography>}
      <AgentRunningBanner loading={loading} remoteRunning={remoteRunning} />
      <AgentPromptCard
        prompt={prompt}
        onPromptChange={(event) => setPrompt(event.target.value)}
        onRun={handleRun}
        loading={loading}
        remoteRunning={remoteRunning}
        versionId={variantId}
        userCompany={userCompany}
        placeholder="e.g. make the hero faster, the ground darker blue, and send iOS players to our App Store page"
        runLabel="Run Agent"
      />
      <AgentResponseCard response={response} canUndo={canUndo} undoing={undoing} onUndo={handleUndo} />
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column" }}>
        <AgentVersionHistory
          versionId={variantId}
          checkpoints={checkpoints}
          historyOpen={historyOpen}
          onToggleHistory={() => setHistoryOpen((value) => !value)}
          restoringId={restoringId}
          loading={loading}
          remoteRunning={remoteRunning}
          onRequestRestore={setConfirmCheckpoint}
        />
        <AgentAdminConsole
          isAdmin={isAdmin}
          consoleOpen={consoleOpen}
          onToggleConsole={() => setConsoleOpen((value) => !value)}
          debugLog={debugLog}
          loading={loading}
          providerName={response && response.providerName}
          agentModel={response && response.model}
          tokenUsage={response && response.tokenUsage}
          usdCost={response && response.usdCost}
          pricingExplanation={response && response.pricingExplanation}
        />
      </div>
      <AgentRestoreConfirmDialog open={Boolean(confirmCheckpoint)} onCancel={() => setConfirmCheckpoint(null)} onConfirm={confirmRestore} />
      <AgentContinueDialog
        open={Boolean(continueInfo)}
        info={continueInfo}
        onContinue={() => {
          setContinueInfo(null);
          void handleRun({ continueRun: true });
        }}
        onKeep={() => setContinueInfo(null)}
        onUndo={async () => {
          setContinueInfo(null);
          await handleUndo();
        }}
        undoDisabled={undoing}
      />
    </div>
  );
}
